// ─────────────────────────────────────────────────────────────────────────────
// SAM2 CLIENT — one-shot body segmentation via cloud
//
// SAM2 (Segment Anything Model 2, Meta 2024) runs on:
//   - Google Cloud Run (sxt-platform endpoint)
//   - AWS Lambda fallback
//   - Replicate API (instant, pay-per-use)
//
// Usage: called ONCE per session when first good frame is captured.
// Returns precise pixel masks for: person, clothing region, face, background.
// These masks are cached and reused each frame (pose landmarks update position).
// ─────────────────────────────────────────────────────────────────────────────

export interface SAM2Mask {
  person:   ImageData;  // full person silhouette
  clothing: ImageData;  // garment region (torso/shoulders)
  face:     ImageData;  // face + neck
  width:    number;
  height:   number;
  confidence: number;
}

export interface SAM2Request {
  imageBase64: string;   // base64-encoded JPEG/PNG
  width:       number;
  height:      number;
  // Optional: hint points to guide SAM2 segmentation
  points?: Array<{ x: number; y: number; label: 1 | 0 }>; // 1=foreground, 0=background
}

export interface SAM2Response {
  masks: {
    person:   number[];  // flat array, 1=mask pixel, length = width*height
    clothing: number[];
    face:     number[];
  };
  confidence: number;
  latency_ms: number;
}

// ── Config — endpoint priority ─────────────────────────────────────────────────
const ENDPOINTS = {
  cloudRun:  "https://sxt-platform-705725624713.us-central1.run.app/api/v1/sam2/segment",
  lambda:    "https://lambda.us-east-1.amazonaws.com/2015-03-31/functions/sxt-tryon-worker/invocations",
  // Replicate (fallback — no self-hosted model needed)
  replicate: "https://api.replicate.com/v1/predictions",
} as const;

const REPLICATE_MODEL_VERSION =
  "fe97b453a6455861e3bac769b441ca1f1086110da7466dbb65cf1eecfd60dc83"; // SAM2 large

// ── Capture current video frame as base64 ────────────────────────────────────
export function captureFrameBase64(
  video: HTMLVideoElement,
  quality = 0.85
): { base64: string; width: number; height: number } {
  const canvas = document.createElement("canvas");
  canvas.width  = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(video, 0, 0);
  const dataUrl = canvas.toDataURL("image/jpeg", quality);
  return {
    base64: dataUrl.split(",")[1],
    width:  canvas.width,
    height: canvas.height,
  };
}

// ── Build SAM2 prompt points from pose landmarks ──────────────────────────────
export function buildPromptPoints(
  landmarks: Array<{ x: number; y: number; visibility?: number }>,
  width: number,
  height: number,
  isMirrored: boolean
): Array<{ x: number; y: number; label: 1 | 0 }> {
  const points: Array<{ x: number; y: number; label: 1 | 0 }> = [];

  // Foreground: torso center, shoulders, hips
  const fgIndices = [11, 12, 23, 24]; // shoulders + hips
  for (const idx of fgIndices) {
    const lm = landmarks[idx];
    if (!lm || (lm.visibility ?? 1) < 0.5) continue;
    points.push({
      x: (isMirrored ? 1 - lm.x : lm.x) * width,
      y: lm.y * height,
      label: 1,
    });
  }

  // Background: corners
  points.push({ x: 5,         y: 5,          label: 0 });
  points.push({ x: width - 5, y: 5,          label: 0 });
  points.push({ x: 5,         y: height - 5, label: 0 });

  return points;
}

// ── Main: run SAM2 segmentation ───────────────────────────────────────────────
export async function runSAM2(
  req: SAM2Request,
  apiToken?: string
): Promise<SAM2Mask | null> {
  // Try Cloud Run first (self-hosted, no cost per call)
  const result = await _tryCloudRun(req, apiToken)
    ?? await _tryReplicate(req, apiToken);

  if (!result) {
    console.warn("[SAM2] All endpoints failed");
    return null;
  }

  return _responseToMask(result, req.width, req.height);
}

async function _tryCloudRun(
  req: SAM2Request,
  token?: string
): Promise<SAM2Response | null> {
  try {
    const res = await fetch(ENDPOINTS.cloudRun, {
      method:  "POST",
      headers: {
        "Content-Type":  "application/json",
        ...(token ? { "Authorization": `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(req),
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) throw new Error(`Cloud Run ${res.status}`);
    return await res.json() as SAM2Response;
  } catch (err) {
    console.warn("[SAM2] Cloud Run failed:", err);
    return null;
  }
}

async function _tryReplicate(
  req: SAM2Request,
  token?: string
): Promise<SAM2Response | null> {
  if (!token) return null; // Replicate requires a key

  try {
    // Start prediction
    const startRes = await fetch(ENDPOINTS.replicate, {
      method:  "POST",
      headers: {
        "Content-Type":  "application/json",
        "Authorization": `Token ${token}`,
      },
      body: JSON.stringify({
        version: REPLICATE_MODEL_VERSION,
        input: {
          image:  `data:image/jpeg;base64,${req.imageBase64}`,
          points_per_side: 32,
          pred_iou_thresh: 0.88,
        },
      }),
    });
    if (!startRes.ok) throw new Error(`Replicate start ${startRes.status}`);
    const prediction = await startRes.json() as { id: string; urls: { get: string } };

    // Poll for result (SAM2 typically takes 2–5s on Replicate)
    for (let i = 0; i < 20; i++) {
      await new Promise(r => setTimeout(r, 500));
      const pollRes = await fetch(prediction.urls.get, {
        headers: { "Authorization": `Token ${token}` },
      });
      const poll = await pollRes.json() as {
        status: string;
        output?: { masks: SAM2Response["masks"]; confidence: number };
      };
      if (poll.status === "succeeded" && poll.output) {
        return { ...poll.output, latency_ms: i * 500 };
      }
      if (poll.status === "failed") break;
    }
    return null;
  } catch (err) {
    console.warn("[SAM2] Replicate failed:", err);
    return null;
  }
}

function _responseToMask(res: SAM2Response, w: number, h: number): SAM2Mask {
  const toImageData = (flat: number[]): ImageData => {
    const img = new ImageData(w, h);
    for (let i = 0; i < flat.length; i++) {
      if (flat[i]) {
        img.data[i * 4]     = 255;
        img.data[i * 4 + 1] = 255;
        img.data[i * 4 + 2] = 255;
        img.data[i * 4 + 3] = 255;
      }
    }
    return img;
  };

  return {
    person:     toImageData(res.masks.person   ?? []),
    clothing:   toImageData(res.masks.clothing ?? []),
    face:       toImageData(res.masks.face     ?? []),
    width:  w,
    height: h,
    confidence: res.confidence ?? 0,
  };
}

// ── Session mask cache ────────────────────────────────────────────────────────
// SAM2 is run once per session. Masks are cached here and reused each frame.
let _cachedMask: SAM2Mask | null = null;
let _maskSessionId = "";

export function getCachedSAM2Mask(): SAM2Mask | null {
  return _cachedMask;
}

export function setCachedSAM2Mask(mask: SAM2Mask, sessionId: string) {
  _cachedMask     = mask;
  _maskSessionId  = sessionId;
}

export function clearSAM2Cache() {
  _cachedMask    = null;
  _maskSessionId = "";
}

export function getSAM2SessionId(): string {
  return _maskSessionId;
}
